import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Pencil, Trash2, X, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ["Φυσική", "Μαθηματικά", "Χημεία"];
const GRADES = ["Γ' Γυμνασίου", "Α' Λυκείου"];
const empty = { subject: "Φυσική", grade: "Γ' Γυμνασίου", topic: "", difficulty: 1, question: "", options: ["", "", "", ""], answer: 0, explanation: "" };
const inp = "w-full h-10 px-3 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary";

function Editor({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setOpt = (i, v) => setF({ ...f, options: f.options.map((o, j) => (j === i ? v : o)) });
  return (
    <form data-testid="quiz-editor" onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="bg-white border-2 border-primary rounded-sm p-4 space-y-3">
      <div className="grid sm:grid-cols-4 gap-3">
        <select data-testid="quiz-editor-subject" className={inp} value={f.subject} onChange={(e) => set("subject", e.target.value)}>{SUBJECTS.map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid="quiz-editor-grade" className={inp} value={f.grade} onChange={(e) => set("grade", e.target.value)}>{GRADES.map((s) => <option key={s}>{s}</option>)}</select>
        <input data-testid="quiz-editor-topic" className={inp} placeholder="Ενότητα *" value={f.topic} onChange={(e) => set("topic", e.target.value)} required />
        <select data-testid="quiz-editor-difficulty" className={inp} value={f.difficulty} onChange={(e) => set("difficulty", Number(e.target.value))}><option value={1}>1 · Βασικό</option><option value={2}>2 · Μεσαίο</option><option value={3}>3 · Απαιτητικό</option></select>
      </div>
      <textarea data-testid="quiz-editor-question" className={`${inp} h-auto py-2`} rows={2} placeholder="Ερώτηση *" value={f.question} onChange={(e) => set("question", e.target.value)} required />
      <div className="grid sm:grid-cols-2 gap-3">
        {f.options.map((o, i) => (
          <label key={i} className={`flex items-center gap-2 border rounded-sm px-2 ${f.answer === i ? "border-emerald-600 bg-emerald-50" : "border-border"}`}>
            <input type="radio" name="answer" data-testid={`quiz-editor-answer-${i}`} checked={f.answer === i} onChange={() => set("answer", i)} className="accent-emerald-600" />
            <input data-testid={`quiz-editor-option-${i}`} className="flex-1 h-10 bg-transparent text-sm focus:outline-none" placeholder={`Επιλογή ${"ΑΒΓΔ"[i]} *`} value={o} onChange={(e) => setOpt(i, e.target.value)} required />
          </label>
        ))}
      </div>
      <input data-testid="quiz-editor-explanation" className={inp} placeholder="Επεξήγηση (προαιρετικό)" value={f.explanation} onChange={(e) => set("explanation", e.target.value)} />
      <div className="flex gap-2">
        <button data-testid="quiz-editor-save" className="inline-flex items-center gap-2 h-10 px-4 bg-primary text-primary-foreground rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:bg-accent"><Save className="h-4 w-4" /> Αποθήκευση</button>
        <button type="button" data-testid="quiz-editor-cancel" onClick={onCancel} className="inline-flex items-center gap-2 h-10 px-4 border border-border rounded-sm font-mono-accent text-xs uppercase tracking-widest"><X className="h-4 w-4" /> Άκυρο</button>
      </div>
    </form>
  );
}

export default function AdminQuiz({ authHeader }) {
  const [qs, setQs] = useState([]);
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState({ subject: "Φυσική", grade: "Γ' Γυμνασίου" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([axios.get(`${API}/admin/quiz/questions`, authHeader), axios.get(`${API}/admin/quiz/results`, authHeader)]);
    setQs(a.data); setResults(b.data);
  };
  useEffect(() => { load().catch(() => toast.error("Αποτυχία φόρτωσης τεστ.")); }, []); // eslint-disable-line

  const save = async (f) => {
    try {
      if (f.id) await axios.put(`${API}/admin/quiz/questions/${f.id}`, f, authHeader);
      else await axios.post(`${API}/admin/quiz/questions`, f, authHeader);
      toast.success("Η ερώτηση αποθηκεύτηκε."); setEditing(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Αποτυχία αποθήκευσης."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Διαγραφή ερώτησης;")) return;
    await axios.delete(`${API}/admin/quiz/questions/${id}`, authHeader); toast.success("Διαγράφηκε."); await load();
  };

  const list = qs.filter((q) => q.subject === filter.subject && q.grade === filter.grade);
  const leads = results.filter((r) => r.lead);

  return (
    <section className="lg:col-span-5 space-y-10" data-testid="admin-quiz">
      <div>
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-2">// Διαγνωστικό Τεστ</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-primary" />Ερωτήσεις ({qs.length})</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="admin-quiz-filter-subject" className={inp + " w-auto"} value={filter.subject} onChange={(e) => setFilter({ ...filter, subject: e.target.value })}>{SUBJECTS.map((s) => <option key={s}>{s}</option>)}</select>
            <select data-testid="admin-quiz-filter-grade" className={inp + " w-auto"} value={filter.grade} onChange={(e) => setFilter({ ...filter, grade: e.target.value })}>{GRADES.map((s) => <option key={s}>{s}</option>)}</select>
            <button data-testid="admin-quiz-add" onClick={() => setEditing({ ...empty, ...filter })} className="inline-flex items-center gap-2 h-10 px-4 bg-primary text-primary-foreground rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:bg-accent"><Plus className="h-4 w-4" /> Νέα ερώτηση</button>
          </div>
        </div>
        {editing && !editing.id && <div className="mb-4"><Editor initial={editing} onSave={save} onCancel={() => setEditing(null)} /></div>}
        <div className="bg-white border border-border rounded-sm divide-y divide-border">
          {list.length === 0 && <p className="p-6 text-sm text-muted-foreground">Καμία ερώτηση για {filter.subject} · {filter.grade}.</p>}
          {list.map((q) => editing?.id === q.id ? (
            <div key={q.id} className="p-3"><Editor initial={editing} onSave={save} onCancel={() => setEditing(null)} /></div>
          ) : (
            <div key={q.id} data-testid={`admin-quiz-q-${q.id}`} className="p-4 flex gap-3">
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">{q.topic} · Δυσκολία {q.difficulty}</div>
                <div className="font-medium mt-0.5">{q.question}</div>
                <div className="text-xs text-emerald-700 mt-1">Σωστή: {"ΑΒΓΔ"[q.answer]}. {q.options[q.answer]}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`admin-quiz-edit-${q.id}`} onClick={() => setEditing(q)} className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`admin-quiz-delete-${q.id}`} onClick={() => remove(q.id)} className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-xl font-bold mb-4">Αποτελέσματα μαθητών <span className="font-mono-accent text-xs text-muted-foreground font-normal">({results.length} τεστ · {leads.length} με στοιχεία)</span></h3>
        <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="admin-quiz-results">
          {results.length === 0 && <p className="p-6 text-sm text-muted-foreground">Δεν έχουν γίνει τεστ ακόμη.</p>}
          {results.slice(0, 50).map((r) => (
            <div key={r.id} data-testid={`admin-quiz-result-${r.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <div className="sm:w-28 shrink-0"><span className="font-heading font-extrabold text-xl">{r.score}/{r.total}</span></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{r.lead ? `${r.lead.name} · ${r.lead.phone} · ${r.lead.email}` : <span className="text-muted-foreground italic">Ανώνυμο (δεν ξεκλείδωσε την ανάλυση)</span>}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{r.subject} · {r.grade} · {new Date(r.created_at).toLocaleString("el-GR")} · {Math.round(r.duration_sec / 60)}'</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
